import React, { useState } from 'react';

import styled from 'styled-components';

import BorderedStyledTable from 'common/Table/BorderedStyledTable';
import HighlightedRowOverlay from 'common/Table/HighlightedRowOverlay';
import { getDifferenceLabel } from 'components/Alerts/utils';
import PillSwitcher from 'components/lib/PillSwitcher';
import { formatPrice } from 'utils/helpers/numbers';

const TableContainer = styled.div`
  position: relative;
  z-index: 0;
`;

const StyledTable = styled(BorderedStyledTable)`
  &&& {
    .ant-table-tbody > tr.my-community-row > td {
      border-color: transparent !important;
      background: transparent !important;
    }

    .ant-table-thead > tr > th.highlighted-column {
      color: var(--dark-blue);
    }

    .ant-table-tbody > tr > td.highlighted-column {
      background: var(--light-blue);
    }

    .ant-table-tbody > tr.summary-row > td {
      background: var(--gray-medium);
    }

    .ant-table-tbody > tr.summary-row > td.highlighted-column {
      background: var(--faded-blue);
    }
  }
`;

const CommunityCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CommunityName = styled.span`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const CommunityType = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const CellStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const PriceText = styled.span`
  font-size: 14px;
  color: var(--text-primary);

  &.summary {
    font-family: var(--font-bold);
  }
`;

const DifferenceText = styled.span<{ $color: string }>`
  font-size: 12px;
  color: ${({ $color }) => $color};
`;

const getPriceText = (value: number | null) =>
  value === null || value === undefined ? '-' : formatPrice(value, '$', 0, 0);

export type ComparisonRow = {
  community_id: number;
  community_name: string;
  is_own: boolean;
};

export type ComparisonCareType<Row extends ComparisonRow> = {
  care_type_id: number;
  care_type: string;
  rows: Row[];
};

export type ComparisonValueColumn<Row extends ComparisonRow> = {
  key: string;
  title: string;
  getValue: (row: Row) => number | null;
  getOwnDifferenceLabel?: () => { color: string; text: string } | null;
  isHighlighted?: boolean;
};

type Props<Row extends ComparisonRow, CareType extends ComparisonCareType<Row>> = {
  careTypes: CareType[];
  getValueColumns: (careType: CareType) => ComparisonValueColumn<Row>[];
  defaultActiveCareTypeId?: number;
  getSummaryRow?: (careType: CareType) => Row | null;
};

const ComparisonTable = <Row extends ComparisonRow, CareType extends ComparisonCareType<Row>>({
  careTypes,
  getValueColumns,
  defaultActiveCareTypeId,
  getSummaryRow,
}: Props<Row, CareType>) => {
  const [activeCareTypeId, setActiveCareTypeId] = useState<number>(
    defaultActiveCareTypeId ?? careTypes[0]?.care_type_id
  );
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);

  const activeCareType = careTypes.find((careType) => careType.care_type_id === activeCareTypeId) ?? careTypes[0];
  if (!activeCareType) return null;

  const ownRow = activeCareType.rows.find((row) => row.is_own);
  const valueColumns = getValueColumns(activeCareType);
  const summaryRow = getSummaryRow?.(activeCareType) ?? null;
  const dataSource = summaryRow ? [...activeCareType.rows, summaryRow] : activeCareType.rows;

  const columns = [
    {
      title: 'Community',
      key: 'community',
      width: '28%',
      render: (_value: unknown, record: Row) => (
        <CommunityCell>
          <CommunityName>{record.community_name}</CommunityName>
          {record !== summaryRow && <CommunityType>{record.is_own ? 'My Community' : 'Competitor'}</CommunityType>}
        </CommunityCell>
      ),
    },
    ...valueColumns.map((column) => ({
      title: column.title,
      key: column.key,
      className: column.isHighlighted ? 'highlighted-column' : undefined,
      render: (_value: unknown, record: Row) => {
        const value = column.getValue(record);
        if (record === summaryRow) {
          return <PriceText className="summary">{getPriceText(value)}</PriceText>;
        }

        const ownValue = ownRow ? column.getValue(ownRow) : null;
        const difference = !record.is_own && value !== null && ownValue !== null ? value - ownValue : null;
        const differenceLabel = record.is_own
          ? (column.getOwnDifferenceLabel?.() ?? null)
          : getDifferenceLabel(difference, value);

        return (
          <CellStack>
            <PriceText>{getPriceText(value)}</PriceText>
            {differenceLabel && <DifferenceText $color={differenceLabel.color}>{differenceLabel.text}</DifferenceText>}
          </CellStack>
        );
      },
    })),
  ];

  return (
    <div>
      {careTypes.length > 1 && (
        <PillSwitcher
          items={careTypes.map((careType) => ({ key: careType.care_type_id, label: careType.care_type }))}
          activeKey={activeCareType.care_type_id}
          onChange={setActiveCareTypeId}
        />
      )}
      <TableContainer ref={setContainerEl}>
        <StyledTable
          columns={columns as any}
          dataSource={dataSource}
          rowKey={((record: Row) => record.community_id) as any}
          rowClassName={(record: any) => {
            if (record === summaryRow) return 'summary-row';
            return record.is_own ? 'my-community-row' : '';
          }}
          pagination={false}
          showSorterTooltip={false}
        />
        <HighlightedRowOverlay containerEl={containerEl} rowSelector="tr.my-community-row" deps={[activeCareType]} />
      </TableContainer>
    </div>
  );
};

export default ComparisonTable;
